/**
 * Semantic colour tokens — what each colour *means*.
 *
 * `app/theme.ts` owns the scales (the brand blue, the slate neutrals). This file
 * owns the assignments, so a concept has exactly one colour everywhere it
 * appears. The rule this enforces: **never recolour the same concept on a
 * different screen.** "Failed" is red in the run log, on the assessment list,
 * and on a connection card, because all three read this module.
 *
 * ## Why the two sets do not overlap
 *
 * Status badges and node badges land side by side in the run log, so their
 * palettes are kept disjoint:
 *
 * | Set | Colours | Reserved for |
 * |---|---|---|
 * | **Status** | green · red · yellow · gray | outcome and health |
 * | **Classification** | gray · cyan · orange · red | how sensitive the data is |
 * | **Node category** | violet · grape · lime · teal · pink · orange | what a node *is* |
 * | **Brand** | blue (hue 214) | the product itself — never a status or a category |
 *
 * That is also why `expired` health is **yellow** rather than the orange it used
 * to be: orange means "target node" now, and a colour cannot mean two things.
 *
 * Vendor identity is a third, separate axis and lives in
 * `features/integration/sourceVisuals.ts` — a Veeva tile is Veeva-coloured
 * regardless of which node category it appears in.
 */

/**
 * Outcome of something that ran — a pipeline run, a node within it, a risk
 * assessment. Keyed loosely because these strings arrive from the API.
 */
export const STATUS_COLOR: Record<string, string> = {
  succeeded: "green",
  failed: "red",
  skipped: "yellow",
  // In flight. Queued is blue — waiting is not a warning — and running is the
  // same yellow the canvas sweeps around a working node, so the badge and the
  // graph say the same thing.
  queued: "blue",
  running: "yellow",
  // Paused on something outside the run — a child pipeline, an approval. Blue
  // like queued, because both mean "nothing is wrong, nothing is happening".
  waiting: "blue",
};

/**
 * Fallback for an outcome the frontend does not recognise — which is also how a
 * future `running` / `queued` state will render until it is given a colour here.
 * Grey is the right default: an unknown outcome is not a failure.
 */
export const STATUS_FALLBACK = "gray";

export function statusColor(status: string): string {
  return STATUS_COLOR[status] ?? STATUS_FALLBACK;
}

/**
 * Connection health, from the last test probe.
 *
 * `unknown` is grey rather than a warning colour on purpose: never-tested is not
 * a fault, and colouring it as one would train operators to ignore the real
 * failures.
 */
export const HEALTH_COLOR: Record<string, string> = {
  ok: "green",
  failing: "red",
  expired: "yellow",
  unknown: "gray",
};

export function healthColor(health: string): string {
  return HEALTH_COLOR[health] ?? "gray";
}

/**
 * Lifecycle state of a controlled object (Development → Validation →
 * Production → Archived).
 *
 * Production is green because it is the *approved* state, not because it is
 * "good news" — the colour carries the same meaning as a passing check
 * elsewhere. Archived uses the neutral dark shade so a withdrawn object reads
 * as inert rather than as a failure.
 */
export const LIFECYCLE_COLOR: Record<string, string> = {
  development: "gray",
  validation: "yellow",
  production: "green",
  archived: "dark",
};

export function lifecycleColor(state: string): string {
  return LIFECYCLE_COLOR[state] ?? "gray";
}

/**
 * Data classification on a connection. Ordered by sensitivity, and coloured so
 * the two regulated classes are visually louder than the two that are not —
 * an operator scanning a list of connections should see PHI before they read
 * the word.
 */
export const CLASSIFICATION_COLOR: Record<string, string> = {
  Public: "gray",
  Confidential: "cyan",
  PII: "orange",
  PHI: "red",
};

export function classificationColor(classification: string): string {
  return CLASSIFICATION_COLOR[classification] ?? "gray";
}
